/**
 * Écran détail dossier admin /admin/projects/:id.
 *
 * Regroupe :
 *  - en-tête (référence + statut + retour),
 *  - infos client (avec lien vers le détail client),
 *  - infos projet,
 *  - changement de statut + timeline d'audit,
 *  - affectation technicien,
 *  - documents (valider / refuser),
 *  - paiements (confirmation),
 *  - rapports + création d'un rapport.
 */
import type {
  DocumentRecord,
  Payment,
  Project,
  ProjectStatus,
} from '@bcl/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Download, Eye, FileText } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import DOMPurify from 'dompurify';
import { Textarea } from '@/components/ui/textarea';
import { DashboardLayout } from '@/components/dashboard/dashboard-layout';
import {
  confirmCardPayment,
  getClientDetail,
  getProjectDetail,
  getProjectDocuments,
  getProjectPayments,
  getProjectTimeline,
  getReportFileUrl,
  listReportsForProject,
  listTechnicians,
  reviewDocument,
  updateProjectStatus,
  validateBankTransfer,
  type Report,
} from '@/features/admin/admin-api';
import {
  assignAppointment,
  getProjectAppointment,
  proposeSlotsForProject,
  type EnrichedAppointment,
} from '@/features/appointments/appointments-api';
import {
  appointmentStatusBadge,
  formatSlot,
} from '@/features/appointments/appointment-labels';
import { getDownloadUrl } from '@/features/documents/documents-api';
import {
  listPartners,
  type PartnerWithProfile,
} from '@/features/partners/partners-api';
import { extractErrorMessage } from '@/features/auth/error-message';
import { InvoiceDownloadButton } from '@/features/payments/invoice-download-button';
import { getStatusColor } from '@/features/dashboard/status-color';
import {
  requestTypeLabels,
  statusLabels,
  typologyLabels,
} from '@/features/projects/labels';

const STATUS_KEYS = Object.keys(statusLabels) as ProjectStatus[];

export function AdminProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const projectId = id ?? '';
  const qc = useQueryClient();

  const project = useQuery({
    queryKey: ['admin', 'project', projectId],
    queryFn: () => getProjectDetail(projectId),
    enabled: !!projectId,
  });

  const client = useQuery({
    queryKey: ['admin', 'project', projectId, 'client'],
    queryFn: () => getClientDetail(project.data!.userId),
    enabled: !!project.data,
  });

  const timeline = useQuery({
    queryKey: ['admin', 'project', projectId, 'timeline'],
    queryFn: () => getProjectTimeline(projectId),
    enabled: !!projectId,
  });

  const docs = useQuery({
    queryKey: ['admin', 'project', projectId, 'documents'],
    queryFn: () => getProjectDocuments(projectId),
    enabled: !!projectId,
  });

  const payments = useQuery({
    queryKey: ['admin', 'project', projectId, 'payments'],
    queryFn: () => getProjectPayments(projectId),
    enabled: !!projectId,
  });

  const reports = useQuery({
    queryKey: ['admin', 'project', projectId, 'reports'],
    queryFn: () => listReportsForProject(projectId),
    enabled: !!projectId,
  });

  const technicians = useQuery({
    queryKey: ['admin', 'technicians'],
    queryFn: listTechnicians,
  });

  const partners = useQuery({
    queryKey: ['admin', 'partners', 'assignable'],
    queryFn: listPartners,
  });

  const appointment = useQuery({
    queryKey: ['admin', 'project', projectId, 'appointment'],
    queryFn: () => getProjectAppointment(projectId),
    enabled: !!projectId,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['admin', 'project', projectId] });
  };

  return (
    <DashboardLayout variant="admin" title="Détail dossier">
      {/* Ordre pensé pour le traitement BCL : on valide le dossier (statut +
          client + projet + documents en consultation rapide), puis on pilote le
          rendez-vous / le technicien, puis le compte rendu, et le paiement tout
          en bas. */}
      <ProjectHeader project={project.data} />

      {/* Blocs métier à gauche (client → projet → docs → RDV → technicien →
          compte rendu → paiement), Statut & timeline en colonne de droite. */}
      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <ClientCard
            client={client.data?.client ?? null}
            isLoading={client.isLoading}
          />

          <ProjectInfoCard project={project.data} />

          <DocumentsCard
            documents={docs.data ?? []}
            onUpdated={() => {
              qc.invalidateQueries({
                queryKey: ['admin', 'project', projectId, 'documents'],
              });
              toast.success('Document mis à jour');
            }}
          />

          <AppointmentCard
            projectId={projectId}
            appointment={appointment.data ?? null}
            onUpdated={() => {
              qc.invalidateQueries({
                queryKey: ['admin', 'project', projectId, 'appointment'],
              });
            }}
          />

          <TechnicianCard
            appointment={appointment.data ?? null}
            technicians={technicians.data ?? []}
            partners={partners.data ?? []}
            onUpdated={() => {
              qc.invalidateQueries({
                queryKey: ['admin', 'project', projectId, 'appointment'],
              });
            }}
          />

          <ReportsCard
            projectId={projectId}
            reports={reports.data ?? []}
            onUpdated={() => {
              invalidate();
            }}
          />

          <PaymentsCard
            payments={payments.data ?? []}
            onUpdated={() => {
              qc.invalidateQueries({
                queryKey: ['admin', 'project', projectId, 'payments'],
              });
              toast.success('Paiement mis à jour');
            }}
          />
        </div>

        <div className="space-y-6">
          <StatusCard
            project={project.data}
            timeline={timeline.data ?? []}
            onUpdated={() => {
              invalidate();
              toast.success('Statut mis à jour');
            }}
          />
        </div>
      </div>
    </DashboardLayout>
  );
}

function ProjectHeader({ project }: { project: Project | undefined }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div>
        <Button asChild variant="ghost" size="sm" className="mb-2">
          <Link to="/admin/projects">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour aux dossiers
          </Link>
        </Button>
        <h2 className="text-lg font-semibold">
          {project ? project.reference : 'Chargement…'}
        </h2>
      </div>
      {project && (
        <Badge
          variant="outline"
          className={getStatusColor(statusLabels[project.status])}
        >
          {statusLabels[project.status]}
        </Badge>
      )}
    </div>
  );
}

function ClientCard({
  client,
  isLoading,
}: {
  client: { id: string; firstName: string; lastName: string; email: string; phone: string; clientType: string | null } | null;
  isLoading: boolean;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Client</CardTitle>
        <CardDescription>Informations du client porteur du dossier.</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading || !client ? (
          <p className="text-sm text-gray-500">Chargement…</p>
        ) : (
          <dl className="grid gap-3 text-sm sm:grid-cols-2">
            <Field label="Nom">
              {client.firstName} {client.lastName}
            </Field>
            <Field label="Email">{client.email}</Field>
            <Field label="Téléphone">{client.phone}</Field>
            <Field label="Type">{client.clientType ?? '—'}</Field>
            <div className="sm:col-span-2">
              <Button asChild variant="outline" size="sm">
                <Link to={`/admin/clients/${client.id}`}>Voir le détail du client</Link>
              </Button>
            </div>
          </dl>
        )}
      </CardContent>
    </Card>
  );
}

function ProjectInfoCard({ project }: { project: Project | undefined }) {
  if (!project) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Projet</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-500">Chargement…</p>
        </CardContent>
      </Card>
    );
  }
  const info = project.info;
  return (
    <Card>
      <CardHeader>
        <CardTitle>Projet</CardTitle>
        <CardDescription>Informations issues du parcours client.</CardDescription>
      </CardHeader>
      <CardContent>
        <dl className="grid gap-3 text-sm sm:grid-cols-2">
          <Field label="Type de demande">
            {requestTypeLabels[project.requestType]}
          </Field>
          <Field label="Typologie">
            {project.typology ? typologyLabels[project.typology] : '—'}
          </Field>
          <Field label="Pack">{project.packCode ?? '—'}</Field>
          <Field label="Créé le">
            {new Date(project.createdAt).toLocaleDateString('fr-FR')}
          </Field>
          {info ? (
            <Field label="Adresse">
              {[info.address.line1, info.address.postalCode, info.address.city]
                .filter(Boolean)
                .join(' · ')}
            </Field>
          ) : null}
          {info ? (
            <Field label="Contact sur place">
              {info.contactName} · {info.contactPhone}
            </Field>
          ) : null}
        </dl>
      </CardContent>
    </Card>
  );
}

function StatusCard({
  project,
  timeline,
  onUpdated,
}: {
  project: Project | undefined;
  timeline: { id: string; status: ProjectStatus; message: string | null; createdAt: string }[];
  onUpdated: () => void;
}) {
  const [nextStatus, setNextStatus] = useState<ProjectStatus | ''>('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [message, setMessage] = useState('');

  const mut = useMutation({
    mutationFn: () =>
      updateProjectStatus(project!.id, {
        status: nextStatus as ProjectStatus,
        message: message.trim() ? message.trim() : undefined,
      }),
    onSuccess: () => {
      setConfirmOpen(false);
      setMessage('');
      setNextStatus('');
      onUpdated();
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Statut & timeline</CardTitle>
        <CardDescription>
          Pilotez l&apos;avancement du dossier et historique des changements.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="next-status">Nouveau statut</Label>
            <select
              id="next-status"
              value={nextStatus}
              onChange={(e) => setNextStatus(e.target.value as ProjectStatus | '')}
              disabled={!project}
              className="h-10 w-72 rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="">Sélectionner…</option>
              {STATUS_KEYS.map((s) => (
                <option key={s} value={s}>
                  {statusLabels[s]}
                </option>
              ))}
            </select>
          </div>
          <Button
            disabled={!nextStatus || !project}
            onClick={() => setConfirmOpen(true)}
          >
            Mettre à jour
          </Button>
        </div>

        <div>
          <h3 className="mb-3 text-sm font-medium">Historique</h3>
          {timeline.length === 0 ? (
            <p className="text-sm text-gray-500">Aucun évènement.</p>
          ) : (
            <ul className="space-y-2">
              {timeline.map((e) => (
                <li
                  key={e.id}
                  className="rounded-md border bg-white p-3 text-sm"
                >
                  <p className="font-medium">{statusLabels[e.status]}</p>
                  {e.message && (
                    <p className="mt-1 text-xs text-gray-600">{e.message}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    {new Date(e.createdAt).toLocaleString('fr-FR')}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </CardContent>

      <Dialog open={confirmOpen} onOpenChange={(o) => !o && setConfirmOpen(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmer le changement de statut</DialogTitle>
            <DialogDescription>
              Le client sera notifié par email.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm">
              Nouveau statut :{' '}
              <strong>
                {nextStatus ? statusLabels[nextStatus as ProjectStatus] : ''}
              </strong>
            </p>
            <div className="space-y-1.5">
              <Label htmlFor="status-message">Message (optionnel)</Label>
              <Textarea
                id="status-message"
                rows={3}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>
              Annuler
            </Button>
            <Button
              onClick={() => mut.mutate()}
              disabled={mut.isPending}
            >
              {mut.isPending ? 'Mise à jour…' : 'Confirmer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

const NEXT_STEP: Record<string, string> = {
  none: 'Proposer deux créneaux au client',
  demande: 'Proposer deux créneaux au client',
  creneaux_proposes: 'En attente du choix du client',
  confirme: 'Attribuer à un technicien ou un partenaire',
  attribue: 'En attente de la réponse du destinataire',
  refuse: 'Refusé — réattribuer en priorité',
  accepte: 'Intervention planifiée',
  realise: 'Intervention réalisée',
  non_effectue: 'Intervention non effectuée',
};

/**
 * Carte « Rendez-vous » : proposer 2 créneaux au client puis voir son choix.
 * L'affectation à un technicien/partenaire se fait dans la carte suivante,
 * disponible une fois le créneau confirmé.
 */
function AppointmentCard({
  projectId,
  appointment,
  onUpdated,
}: {
  projectId: string;
  appointment: EnrichedAppointment | null;
  onUpdated: () => void;
}) {
  const [slot1, setSlot1] = useState('');
  const [slot2, setSlot2] = useState('');

  const status = appointment?.status ?? 'none';
  const canPropose =
    status === 'none' || status === 'demande' || status === 'creneaux_proposes';

  const proposeMut = useMutation({
    mutationFn: () =>
      proposeSlotsForProject(projectId, {
        slots: [
          new Date(slot1).toISOString(),
          ...(slot2 ? [new Date(slot2).toISOString()] : []),
        ],
      }),
    onSuccess: () => {
      toast.success('Créneaux proposés au client');
      setSlot1('');
      setSlot2('');
      onUpdated();
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle>Rendez-vous</CardTitle>
          {status !== 'none' && (
            <Badge variant="outline" className={appointmentStatusBadge[appointment!.status].cls}>
              {appointmentStatusBadge[appointment!.status].label}
            </Badge>
          )}
        </div>
        <CardDescription>
          Prochaine étape : <strong>{NEXT_STEP[status]}</strong>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {appointment?.confirmedSlot && (
          <p className="text-sm">
            Créneau confirmé : <strong>{formatSlot(appointment.confirmedSlot)}</strong>
          </p>
        )}
        {status === 'creneaux_proposes' && (
          <div className="rounded-md border bg-gray-50 p-3 text-sm">
            Créneaux proposés, en attente du choix du client :
            <ul className="mt-1 list-disc pl-5">
              {appointment!.proposedSlots.map((s) => (
                <li key={s}>{formatSlot(s)}</li>
              ))}
            </ul>
          </div>
        )}
        {appointment?.refusalReason && (
          <p className="text-sm text-red-700">
            Motif de refus : {appointment.refusalReason}
          </p>
        )}

        {canPropose && (
          <div className="space-y-2">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="rdv-slot1">Créneau 1</Label>
                <Input
                  id="rdv-slot1"
                  type="datetime-local"
                  value={slot1}
                  onChange={(e) => setSlot1(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="rdv-slot2">Créneau 2 (optionnel)</Label>
                <Input
                  id="rdv-slot2"
                  type="datetime-local"
                  value={slot2}
                  onChange={(e) => setSlot2(e.target.value)}
                />
              </div>
            </div>
            <Button
              size="sm"
              disabled={!slot1 || proposeMut.isPending}
              onClick={() => proposeMut.mutate()}
            >
              {status === 'creneaux_proposes'
                ? 'Renvoyer des créneaux'
                : 'Proposer les créneaux'}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Carte « Technicien / partenaire » : grisée tant que le client n'a pas
 * confirmé un créneau (on n'affecte pas avant son accord). Une fois confirmé,
 * on attribue à un technicien BCL ou un partenaire ; ensuite on suit sa réponse.
 */
function TechnicianCard({
  appointment,
  technicians,
  partners,
  onUpdated,
}: {
  appointment: EnrichedAppointment | null;
  technicians: { id: string; firstName: string; lastName: string }[];
  partners: PartnerWithProfile[];
  onUpdated: () => void;
}) {
  const [assignee, setAssignee] = useState('');
  const status = appointment?.status ?? 'none';
  const canAssign = status === 'confirme' || status === 'refuse';
  const assigned =
    status === 'attribue' ||
    status === 'accepte' ||
    status === 'realise' ||
    status === 'non_effectue';
  const active = canAssign || assigned;

  const assignMut = useMutation({
    mutationFn: () => assignAppointment(appointment!.id, assignee),
    onSuccess: () => {
      toast.success('Rendez-vous attribué');
      setAssignee('');
      onUpdated();
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  const assigneeId = appointment?.assigneeId ?? null;
  const tech = assigneeId
    ? technicians.find((t) => t.id === assigneeId)
    : undefined;
  const partner = assigneeId
    ? partners.find((p) => p.id === assigneeId)
    : undefined;
  const assigneeName = tech
    ? `BCL · ${tech.firstName} ${tech.lastName}`
    : partner
      ? `Partenaire · ${partner.profile?.companyName ?? `${partner.firstName} ${partner.lastName}`}`
      : null;

  return (
    <Card className={active ? undefined : 'opacity-60'}>
      <CardHeader>
        <CardTitle>Technicien / partenaire</CardTitle>
        <CardDescription>
          {active
            ? 'Affectez l’intervention à un technicien BCL ou à un partenaire.'
            : 'Disponible une fois le créneau confirmé par le client.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {!active && (
          <p className="text-sm text-gray-500">
            On n’affecte pas de technicien avant que le client ait choisi son
            créneau.
          </p>
        )}

        {status === 'refuse' && (
          <p className="text-sm text-red-700">
            Refusé par le destinataire — à réattribuer en priorité.
          </p>
        )}

        {canAssign && (
          <div className="flex flex-wrap items-end gap-2">
            <div className="space-y-1.5">
              <Label htmlFor="assignee">Affecter à</Label>
              <select
                id="assignee"
                value={assignee}
                onChange={(e) => setAssignee(e.target.value)}
                className="h-9 rounded-md border border-input bg-background px-2 text-sm"
              >
                <option value="">— Choisir —</option>
                <optgroup label="Techniciens">
                  {technicians.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.firstName} {t.lastName}
                    </option>
                  ))}
                </optgroup>
                <optgroup label="Partenaires">
                  {partners.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.profile?.companyName ?? `${p.firstName} ${p.lastName}`}
                    </option>
                  ))}
                </optgroup>
              </select>
            </div>
            <Button
              size="sm"
              disabled={!assignee || assignMut.isPending}
              onClick={() => assignMut.mutate()}
            >
              Affecter
            </Button>
          </div>
        )}

        {assigned && (
          <p className="text-sm">
            Affecté à <strong>{assigneeName ?? '—'}</strong>
            {status === 'attribue' && ' — en attente de son acceptation.'}
            {status === 'accepte' && ' — accepté, intervention planifiée.'}
            {status === 'realise' && ' — intervention réalisée.'}
            {status === 'non_effectue' && ' — intervention non effectuée.'}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function DocumentsCard({
  documents,
  onUpdated,
}: {
  documents: DocumentRecord[];
  onUpdated: () => void;
}) {
  const [reason, setReason] = useState<Record<string, string>>({});
  const mut = useMutation({
    mutationFn: ({
      id,
      action,
      reasonText,
    }: {
      id: string;
      action: 'valide' | 'refuse';
      reasonText?: string;
    }) => reviewDocument(id, { status: action, reason: reasonText }),
    onSuccess: onUpdated,
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  async function openDoc(id: string, download: boolean) {
    try {
      const url = await getDownloadUrl(id);
      if (download) {
        const a = document.createElement('a');
        a.href = url;
        a.download = '';
        a.click();
      } else {
        window.open(url, '_blank', 'noopener');
      }
    } catch (e) {
      toast.error(extractErrorMessage(e));
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Documents</CardTitle>
        <CardDescription>
          {documents.length} document{documents.length > 1 ? 's' : ''} —
          consultez, téléchargez, validez ou refusez.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {documents.length === 0 ? (
          <p className="text-sm text-gray-500">Aucun document.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Fichier</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {documents.map((d) => {
                const badge =
                  DOC_STATUS_BADGE[d.status] ?? {
                    label: d.status,
                    cls: 'bg-gray-100 text-gray-700',
                  };
                return (
                  <TableRow key={d.id}>
                    <TableCell className="font-medium">{d.filename}</TableCell>
                    <TableCell className="capitalize">
                      {d.documentType.replace(/_/g, ' ')}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={badge.cls}>
                        {badge.label}
                      </Badge>
                      {d.reviewReason && (
                        <p className="mt-1 text-xs text-gray-500">
                          {d.reviewReason}
                        </p>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex flex-wrap items-center justify-end gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openDoc(d.id, false)}
                        >
                          <Eye className="mr-1 h-4 w-4" />
                          Consulter
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openDoc(d.id, true)}
                          aria-label={`Télécharger ${d.filename}`}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        <Input
                          aria-label={`Motif refus ${d.filename}`}
                          placeholder="Motif refus"
                          value={reason[d.id] ?? ''}
                          onChange={(e) =>
                            setReason((p) => ({ ...p, [d.id]: e.target.value }))
                          }
                          className="h-8 w-36"
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          className="bg-green-50 text-green-800"
                          disabled={mut.isPending || d.status === 'valide'}
                          onClick={() => mut.mutate({ id: d.id, action: 'valide' })}
                        >
                          Valider
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="bg-red-50 text-red-800"
                          disabled={mut.isPending}
                          onClick={() =>
                            mut.mutate({
                              id: d.id,
                              action: 'refuse',
                              reasonText: reason[d.id] || 'Non précisé',
                            })
                          }
                        >
                          Refuser
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

const DOC_STATUS_BADGE: Record<string, { label: string; cls: string }> = {
  en_attente: { label: 'En attente', cls: 'bg-amber-100 text-amber-800' },
  valide: { label: 'Validé', cls: 'bg-green-100 text-green-800' },
  refuse: { label: 'Refusé', cls: 'bg-red-100 text-red-800' },
  a_completer: { label: 'À compléter', cls: 'bg-blue-100 text-blue-800' },
};

function PaymentsCard({
  payments,
  onUpdated,
}: {
  payments: Payment[];
  onUpdated: () => void;
}) {
  const confirmCard = useMutation({
    mutationFn: (p: Payment) =>
      confirmCardPayment({
        paymentId: p.id,
        famReference: p.famReference ?? 'manual',
      }),
    onSuccess: onUpdated,
    onError: (e) => toast.error(extractErrorMessage(e)),
  });
  const validateTransfer = useMutation({
    mutationFn: (p: Payment) =>
      validateBankTransfer({
        paymentId: p.id,
        bankReference: p.bankReference ?? 'manual',
      }),
    onSuccess: onUpdated,
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Paiements</CardTitle>
        <CardDescription>{payments.length} paiements liés.</CardDescription>
      </CardHeader>
      <CardContent>
        {payments.length === 0 ? (
          <p className="text-sm text-gray-500">Aucun paiement.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Méthode</TableHead>
                <TableHead>Montant TTC</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {payments.map((p) => (
                <TableRow key={p.id}>
                  <TableCell className="capitalize">{p.method}</TableCell>
                  <TableCell>{p.amountTtc} €</TableCell>
                  <TableCell>
                    <Badge variant="outline">{p.status.replace('_', ' ')}</Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    {p.status === 'en_attente' && p.method === 'carte' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => confirmCard.mutate(p)}
                      >
                        Confirmer carte
                      </Button>
                    )}
                    {p.status === 'en_attente' && p.method === 'virement' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => validateTransfer.mutate(p)}
                      >
                        Valider virement
                      </Button>
                    )}
                    {p.status === 'paye' && (
                      <InvoiceDownloadButton
                        paymentId={p.id}
                        label="Facture PDF"
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Compte rendu d'intervention : le technicien le dépose, BCL le consulte et le
 * valide. La carte est grisée tant qu'aucun compte rendu n'est déposé. Pas de
 * création côté admin (c'est une action technicien).
 */
function ReportsCard({
  projectId,
  reports,
  onUpdated,
}: {
  projectId: string;
  reports: Report[];
  onUpdated: () => void;
}) {
  const hasReports = reports.length > 0;

  async function openReportFile(reportId: string) {
    try {
      const url = await getReportFileUrl(reportId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      toast.error(extractErrorMessage(e));
    }
  }

  const validateMut = useMutation({
    mutationFn: () =>
      updateProjectStatus(projectId, {
        status: 'termine',
        message: 'Compte rendu validé',
      }),
    onSuccess: () => {
      toast.success('Compte rendu validé');
      onUpdated();
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  return (
    <Card className={hasReports ? undefined : 'opacity-60'}>
      <CardHeader>
        <CardTitle>Compte rendu d&apos;intervention</CardTitle>
        <CardDescription>
          {hasReports
            ? 'Consultez le compte rendu du technicien, puis validez-le.'
            : 'En attente du compte rendu du technicien.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {hasReports ? (
          <>
            <ul className="space-y-3">
              {reports.map((r) => (
                <li key={r.id} className="rounded-md border bg-white p-3">
                  <p className="flex items-center gap-2 font-medium">
                    <FileText className="h-4 w-4" />
                    {r.title}
                  </p>
                  {r.content && (
                    <div
                      className="mt-1 text-sm text-gray-700 [&_h3]:font-semibold [&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:list-disc [&_ul]:pl-5"
                      // Note HTML déjà assainie côté serveur ; on ré-assaini au
                      // rendu (DOMPurify) par défense en profondeur.
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(r.content) }}
                    />
                  )}
                  {r.fileName && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-2"
                      onClick={() => openReportFile(r.id)}
                    >
                      <FileText className="mr-2 h-4 w-4" />
                      Télécharger le PDF
                    </Button>
                  )}
                  <p className="mt-2 text-xs text-gray-500">
                    {new Date(r.createdAt).toLocaleString('fr-FR')}
                  </p>
                </li>
              ))}
            </ul>
            <Button
              disabled={validateMut.isPending}
              onClick={() => validateMut.mutate()}
            >
              {validateMut.isPending ? 'Validation…' : 'Valider le compte rendu'}
            </Button>
          </>
        ) : (
          <p className="text-sm text-gray-500">
            Le technicien n&apos;a pas encore déposé de compte rendu. Cette étape
            s&apos;activera dès qu&apos;il l&apos;aura fait.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-gray-500">{label}</dt>
      <dd className="mt-0.5 text-sm text-gray-900">{children}</dd>
    </div>
  );
}
